import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { CardBody, CardHeader, Meter, Stack, Text } from 'grommet';
import { Checkmark } from 'grommet-icons';
import leaflet from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './CurrentFacilityLocationsChart.css';
import { Box, Card, CardProps } from '/src/components';
import { config } from '/src/config';
import { useDashboardStore } from '/src/context';
import { DashboardData } from '/src/lib/models';
import { Coordinates, CurrentFacilityLocationsChartData } from '/src/lib/types';

const markerIcon = leaflet.divIcon({
  className: 'fuse-map-marker',
  html: `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
      <defs>
        <filter id="fuse-marker-shadow" x="-30%" y="-10%" width="160%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-opacity="0.3"/>
        </filter>
      </defs>
      <path filter="url(#fuse-marker-shadow)" fill="#EAAD47" stroke="#fff" stroke-width="2"
        d="M14 1C7.4 1 2 6.4 2 13c0 9 12 22 12 22s12-13 12-22c0-6.6-5.4-12-12-12z"/>
      <circle cx="14" cy="13" r="4" fill="#fff"/>
    </svg>
  `,
  iconSize: [28, 36],
  iconAnchor: [14, 36],
  popupAnchor: [0, -32],
});

export const CurrentFacilityLocationsChart: React.FC<CurrentFacilityLocationsChartProps> = (props) => {
  /* Props */
  const { height, ...cardProps } = props;

  /* Context */
  const dashboardStore = useDashboardStore();
  const { t } = useTranslation();

  /* State */
  const [isLoading, setIsLoading] = useState(true);
  const [mapData, setMapData] = useState<CurrentFacilityLocationsChartData>([]);

  /* Memos */
  const cardHeight = useRef('52.125rem');
  const defaultMapCoords = useRef<Coordinates>([40.103, -122.217]); // halfway between San Diego and Seattle
  const clientId = dashboardStore.dashboardData?.client_id;

  /* Effects */
  useEffect(() => {
    if (!dashboardStore.dashboardData?.facilities) return;

    if (isLoading) {
      const chartData = dashboardStore.dashboardData.facilities
        .map((facility) => ({
          id: facility.id,
          name: facility.name,
          address_1: `${facility.address.line1}${facility.address.line2 ? ` ${facility.address.line2}` : ''}`,
          address_2: `${facility.address.city}, ${facility.address.region_short_code} ${facility.address.post_code}`,
          coords:
            facility.address.latitude && facility.address.longitude
              ? ([facility.address.latitude, facility.address.longitude] as Coordinates)
              : undefined,
        }))
        .filter((d) => !!d);

      setMapData(chartData);
      setIsLoading(false);
    }
  }, [isLoading, dashboardStore.dashboardData]);

  /* Render */
  return (
    <Card height={height || cardHeight.current} {...cardProps}>
      <CardHeader>
        <Text size="xlarge">{t('dashboard:currentFacilityLocations')}</Text>
      </CardHeader>
      <CardBody pad="1.5rem">
        {isLoading && dashboardStore.dashboardData?.facilities && (
          <Box height="100vh" fill="horizontal" justify="center" align="center">
            <Stack anchor="center">
              <Meter
                size={`${16 * 10}px`}
                value={dashboardStore.dashboardData.facilities.length * 10}
                type="circle"
                color="green"
              />
              <Checkmark size={`${16 * 5}px`} color="green" />
            </Stack>
          </Box>
        )}
        {!isLoading && !!mapData && (
          <MapContainer center={mapData[0]?.coords ?? defaultMapCoords.current} zoom={5}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${config.maps.cartoBasemapsApiKey}`}
              subdomains="abcd"
              maxZoom={20}
            />
            {mapData.map((f, i) => {
              return f.coords ? (
                <Marker key={`${f.name}-${i}`} position={f.coords} icon={markerIcon}>
                  <Popup>
                    <Box>
                      {f.name && (
                        <Text margin={{ bottom: '0.5rem' }} size="1.25rem">
                          {f.name}
                        </Text>
                      )}
                      <Link to={`/clients/${clientId}/facilities/${f.id}`}>
                        <Text size="1rem" style={{ display: 'block' }}>
                          {f.address_1}
                        </Text>
                        <Text size="1rem" style={{ display: 'block' }}>
                          {f.address_2}
                        </Text>
                      </Link>
                    </Box>
                  </Popup>
                </Marker>
              ) : undefined;
            })}
          </MapContainer>
        )}
      </CardBody>
    </Card>
  );
};

export type CurrentFacilityLocationsChartProps = CardProps & {
  data: DashboardData;
};
